// @ts-nocheck
import React, { useEffect, useLayoutEffect, useRef, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { MoreVertical } from 'lucide-react';

const MENU_WIDTH_PX = 208;

export type TableKebabMenuEntry =
  | { type: 'separator' }
  | {
      type: 'item';
      id: string;
      label: string;
      description?: string;
      icon: React.ReactNode;
      onClick: () => void;
      disabled?: boolean;
      title?: string;
      /** default = emerald hover; danger = red; warning = amber; neutral = slate */
      tone?: 'default' | 'danger' | 'warning' | 'neutral';
    };

function itemToneClasses(tone: string | undefined, disabled: boolean) {
  if (disabled) {
    return 'cursor-not-allowed opacity-45 hover:bg-transparent hover:text-slate-700';
  }
  switch (tone) {
    case 'danger':
      return 'hover:bg-red-50 hover:text-red-800';
    case 'warning':
      return 'hover:bg-amber-50 hover:text-amber-900';
    case 'neutral':
      return 'hover:bg-slate-100 hover:text-slate-900';
    default:
      return 'hover:bg-emerald-50 hover:text-emerald-900';
  }
}

const baseItemClass =
  'flex w-full items-center gap-2.5 px-3 py-2.5 text-left text-sm text-slate-700 transition-colors focus-visible:outline-none';

/**
 * Kebab (⋮) menu for table rows: fixed portal + backdrop so parent `overflow-x-auto` does not clip the menu.
 */
export default function TableRowKebabMenu({
  isOpen,
  onOpenChange,
  ariaLabel = 'Row actions',
  entries,
}: {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  ariaLabel?: string;
  entries: TableKebabMenuEntry[];
}) {
  const btnRef = useRef(null);
  const [coords, setCoords] = useState({ top: 0, left: 0 });

  const syncPosition = useCallback(() => {
    const el = btnRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const pad = 8;
    let left = r.right - MENU_WIDTH_PX;
    if (left < pad) left = pad;
    if (left + MENU_WIDTH_PX > window.innerWidth - pad) {
      left = window.innerWidth - MENU_WIDTH_PX - pad;
    }
    setCoords({ top: r.bottom + 6, left });
  }, []);

  useLayoutEffect(() => {
    if (!isOpen) return;
    syncPosition();
    window.addEventListener('scroll', syncPosition, true);
    window.addEventListener('resize', syncPosition);
    return () => {
      window.removeEventListener('scroll', syncPosition, true);
      window.removeEventListener('resize', syncPosition);
    };
  }, [isOpen, syncPosition]);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => {
      if (e.key === 'Escape') onOpenChange(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen, onOpenChange]);

  return (
    <>
      <div className="flex justify-end">
        <button
          ref={btnRef}
          type="button"
          onClick={() => onOpenChange(!isOpen)}
          className={`inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500/45 focus-visible:ring-offset-1 ${
            isOpen
              ? 'border-emerald-300 bg-emerald-50 text-emerald-800'
              : 'border-slate-200/90 bg-white text-slate-600 hover:bg-slate-50 hover:text-slate-900'
          }`}
          aria-expanded={isOpen}
          aria-haspopup="menu"
          aria-label={ariaLabel}
        >
          <MoreVertical size={20} strokeWidth={2} aria-hidden />
        </button>
      </div>
      {isOpen &&
        createPortal(
          <>
            <div className="fixed inset-0 z-[100] bg-transparent" aria-hidden onClick={() => onOpenChange(false)} />
            <div
              role="menu"
              className="fixed z-[101] rounded-lg border border-slate-200 bg-white py-1 shadow-xl ring-1 ring-black/5"
              style={{ top: coords.top, left: coords.left, width: MENU_WIDTH_PX }}
            >
              {entries.map((entry, i) => {
                if (entry.type === 'separator') {
                  return <div key={`sep-${i}`} className="my-1 h-px bg-slate-100" role="separator" />;
                }
                const tone = entry.tone ?? 'default';
                const disabled = !!entry.disabled;
                return (
                  <button
                    key={entry.id}
                    type="button"
                    role="menuitem"
                    disabled={disabled}
                    title={entry.title}
                    className={`${baseItemClass} ${itemToneClasses(tone, disabled)} ${
                      !disabled && tone === 'default' ? 'focus-visible:bg-emerald-50' : ''
                    } ${!disabled && tone === 'neutral' ? 'focus-visible:bg-slate-100' : ''}`}
                    onClick={() => {
                      if (disabled) return;
                      entry.onClick();
                      onOpenChange(false);
                    }}
                  >
                    <span className="shrink-0 text-slate-500">{entry.icon}</span>
                    <span className="flex min-w-0 flex-col gap-0.5 text-left">
                      <span className="font-medium">{entry.label}</span>
                      {entry.description ? (
                        <span className="text-[11px] font-normal text-slate-500">{entry.description}</span>
                      ) : null}
                    </span>
                  </button>
                );
              })}
            </div>
          </>,
          document.body
        )}
    </>
  );
}
